import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { useAppSelector } from '../../data/store'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import { trackingEvents } from '../utils/trackingEvents'

type Props = {
  onSubmit: (event: { preventDefault: () => void }) => void
  renderCreditContainer: () => void
  stepProgression: React.ReactElement
  hasPendingDocument: boolean
}

function FinalDecisionManualRevision({ onSubmit, stepProgression, hasPendingDocument, renderCreditContainer }: Props) {
  const creditApplication = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatchEffect = useSideEffect()
  const [isSubmitted, setSubmitted] = React.useState<boolean>(false)
  const [toggle, setToggle] = React.useState<boolean>(false)
  const mustShowRetry =
    creditApplication?.applicantHasLockedCreditReport || creditApplication?.coApplicantHasLockedCreditReport

  function innerSubmit(event: { preventDefault: () => void }) {
    setSubmitted(true)
    onSubmit(event)
  }

  const onAcceptHardHitConditions = () => {
    dispatchEffect(creditApplicationEffects.postQualify(id as string)).then(() => {
      navigate(`/creditApplication/${id as string}`, { replace: true })
    })
  }

  const cancelMyApp = (event: { preventDefault: () => void }) => {
    trackingEvents.withdrewManualRevision({
      credit_application_id: id as string,
      reference_number: creditApplication?.referenceNumber as number,
      amount: creditApplication?.finalDecision.maxAmountFinanced,
    })
    navigate(`/creditApplication/${id as string}/reportFeedback`, { replace: true })
    event.preventDefault()
  }

  const removeCoApplicant = () => {
    dispatchEffect(creditApplicationEffects.removeCoApplicant(id as string)).then(() =>
      navigate(`/creditApplication/${id as string}`, { replace: true }),
    )
  }

  const getMessage = () => {
    if (hasPendingDocument) {
      return (
        <>
          <h1>{t('decision.manualRevision.header')}</h1>
          <div className="paragraph">
            <p>{t('decision.manualRevision.message')}</p>
          </div>
          <button type="submit" className="btn btn-blue" disabled={isSubmitted}>
            <span>{t('decision.manualRevision.uploadDocuments')}</span>
            <i className="fa-regular fa-arrow-right" />
          </button>
        </>
      )
    }
    if (mustShowRetry) {
      return (
        <>
          <h1>{t('decision.lockedCreditReport.header')}</h1>
          <ul className="h3" style={{ margin: '2rem auto' }}>
            {creditApplication?.applicantHasLockedCreditReport && (
              <li
                style={{
                  color: '#08abf0',
                  border: '1px solid #08abf0',
                  borderRadius: '1rem',
                  padding: '1rem',
                  margin: '1rem auto',
                }}
              >
                <Trans
                  i18nKey="decision.lockedCreditReport.message"
                  values={{
                    applicantType: t('common.applicant'),
                  }}
                />
              </li>
            )}
            {creditApplication?.coApplicantHasLockedCreditReport && (
              <li
                style={{
                  color: '#08abf0',
                  border: '1px solid #08abf0',
                  borderRadius: '1rem',
                  padding: '1rem',
                  margin: '1rem auto',
                }}
              >
                <Trans
                  i18nKey="decision.lockedCreditReport.message"
                  values={{
                    applicantType: t('common.coapplicant'),
                  }}
                />
              </li>
            )}
          </ul>
          <h3>
            {t('decision.lockedCreditReport.main')}
            <span // eslint-disable-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
              style={{ textDecoration: 'underline', cursor: 'pointer', color: '#08abf0' }}
              onClick={() => setToggle(!toggle)}
            >
              {t('decision.lockedCreditReport.creditReportProvider')}
            </span>
          </h3>
          {toggle && (
            <div style={{ margin: '1rem auto' }}>
              <p
                style={{
                  fontStyle: 'italic',
                  border: '1px solid #08abf0',
                  borderRadius: '1.5rem',
                  textAlign: 'center',
                  padding: '1.5rem',
                }}
              >
                {t('decision.lockedCreditReport.contactEquifax')}
              </p>
            </div>
          )}
          <h3>{t('decision.lockedCreditReport.footer')}</h3>
        </>
      )
    }
    return (
      <>
        <h1>{t<string>('feedback.ongoingAnalysis')}</h1>
        <div className="button-group-2">
          <div className="item">
            <button type="button" className="btn" disabled={isSubmitted} onClick={renderCreditContainer}>
              <span>{t<string>('common.updateDecision')}</span>
              <i className="fa-regular fa-check" />
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <main className="general-message" style={{ paddingTop: '15rem' }}>
      {stepProgression}
      <form onSubmit={innerSubmit}>
        <h4 className="subtitle">{t<string>('common.financeApplication')}</h4>
        {getMessage()}
        {mustShowRetry && (
          <div style={{ marginTop: '3rem' }}>
            <form onSubmit={onAcceptHardHitConditions}>
              <p style={{ fontWeight: 'bold' }}>{t('prequalification.pullCreditReport')}</p>
              <div className="control-group">
                <input
                  required
                  type="checkbox"
                  id="acceptConditions"
                  onChange={(e) => {
                    if (e.currentTarget.checked === true) e.currentTarget.setCustomValidity('')
                  }}
                  onInvalid={(e) => e.currentTarget.setCustomValidity(t<string>('common.acceptConditions'))}
                />
                <label style={{ fontSize: '1.8rem', fontWeight: 'inherit' }} title="test" htmlFor="acceptConditions">
                  {t('prequalification.acceptConditions')}
                </label>
              </div>
              <div
                className={creditApplication?.coApplicantHasLockedCreditReport ? 'button-group-3' : 'button-group-2'}
              >
                <div className="item">
                  <button type="button" className="btn" onClick={cancelMyApp}>
                    <span>{t<string>('common.cancelMyApp')}</span>
                    <i className="fa-regular fa-ban" />
                  </button>
                </div>
                {creditApplication?.coApplicantHasLockedCreditReport && (
                  <div className="item">
                    <button type="button" className="btn" onClick={removeCoApplicant}>
                      <span>{t<string>('common.removeCoapplicant')}</span>
                      <i className="fa-regular fa-user-times" />
                    </button>
                  </div>
                )}
                <div className="item">
                  <button type="submit" className="btn btn-blue">
                    <span>{t('common.submit')}</span>
                    <i className="fa-regular fa-arrow-right" />
                  </button>
                </div>
              </div>
            </form>
            <div style={{ borderTop: '1px solid black', marginTop: '5rem' }}>
              <p style={{ marginTop: '2rem' }}>{t('prequalification.priorToQualifyNotes.2')}</p>
            </div>
          </div>
        )}
      </form>
    </main>
  )
}

export default FinalDecisionManualRevision
